import React from 'react'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Link,
  type StyleProps,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import FTGTooltip from '@core/components/FTGTooltip'
import { addSnack } from '@core/snack/snack-state'
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query'

import * as exportAPI from './export-api'
import { useExportJobs } from './export-items-state'
import * as queries from './export-queries'
import { commonExportStyles, ExportHeader, getProgress } from './export-result-common'
import WebhookExportJobRow from './export-result-webhook-status'
import { getErrorMessage } from './export-utils'

const styles = {
  tabTitle: {
    typography: 'h5',
    mt: 2,
    mb: 4,
    px: 3,
  },
  ...commonExportStyles,
} satisfies StyleProps
type StatusLabelKey = 'completed' | 'failed' | 'completed_with_issues' | 'waiting_user_input'
const statusLabelKeyMap: Record<string, StatusLabelKey> = {
  completed: 'completed',
  failed: 'failed',
  completed_with_issues: 'completed_with_issues',
  waiting_user_input: 'waiting_user_input',
}

type ExportJobRowProps = {
  jobBasic: queries.ExportJobBasic
  isExpanded: boolean
}

type JobErrorProps = {
  error?: string
  jobDetails?: queries.ExportJobDetails
}

const JobError = ({ jobDetails, error }: JobErrorProps) => {
  const { t } = useTranslation('legacy')
  const { title, message } = getErrorMessage(jobDetails?.errorDetails)
  let errorMessage = message

  if (jobDetails && jobDetails.totalFailed > 0) {
    errorMessage = t('export.check_issues_tab')
  }

  if (error) {
    errorMessage = t('export.failed_to_load_details')
  }

  return (
    <Alert severity="error">
      <AlertTitle>{title}</AlertTitle>
      {errorMessage}
    </Alert>
  )
}

const ExportJobRow = ({ jobBasic, isExpanded }: ExportJobRowProps) => {
  const { t } = useTranslation('legacy', { keyPrefix: 'export' })
  const [updateDialog, isChecked] = useExportJobs((state) => [
    state.updateDialog,
    state.exportDialog.selectedJobIds.has(jobBasic.id),
  ])

  const exportDetails = exportAPI.useGetExportDetails(jobBasic.id, isExpanded)

  const onContinue = exportAPI.useExportApi().continueExport

  const archiveExportJobs = useMutationState({
    filters: {
      status: 'pending',
      mutationKey: exportAPI.exportItemsKeys.archiveExportJobs,
    },
  })

  const isArchiving = archiveExportJobs.length > 0

  const exportJob = exportDetails.data?.exportJob
  const issuesCount = exportJob?.failedItems.length || 0

  const status = exportJob?.status || jobBasic.status

  const progress = getProgress(exportJob)
  const destination = exportJob?.data?.destination_url || ''
  const isFailed = status === 'failed'
  const hasIssues = status === 'waiting_user_input'

  const showContent = Boolean(
    !isFailed && isExpanded && exportDetails.data && !onContinue.isPending,
  )
  const showError = Boolean(
    isExpanded && (isFailed || exportDetails.error) && !exportDetails.isLoading,
  )

  const handleCheck = () => {
    if (isChecked) {
      updateDialog((state) => {
        state.selectedJobIds.delete(jobBasic.id)
      })
    } else {
      updateDialog((state) => {
        state.selectedJobIds.add(jobBasic.id)
      })
    }
  }

  return (
    <Box my={2} bgcolor="background.grey" p={2} mx={3} borderRadius={2}>
      <Box aria-label="export-job-header" sx={styles.exportJobHeader}>
        <Checkbox
          onChange={handleCheck}
          checked={isChecked}
          color="secondary"
          size="small"
          disabled={isArchiving}
        />
        <ExportHeader
          job={jobBasic}
          showBadge={isFailed || hasIssues}
          badgeColor={isFailed ? 'error' : 'warning'}
        />
        <IconButton
          disabled={exportDetails.isLoading}
          onClick={() =>
            updateDialog({
              // set it as empty string to not trigger the useEffect that auto selects the first job
              selectedJobId: isExpanded ? '' : jobBasic.id,
            })
          }
        >
          {isExpanded ? (
            exportDetails.isLoading || onContinue.isPending ? (
              <CircularProgress size={20} color="secondary" />
            ) : (
              <ExpandLess />
            )
          ) : (
            <ExpandMore />
          )}
        </IconButton>
      </Box>

      <Collapse in={showError} timeout="auto" unmountOnExit>
        <JobError jobDetails={exportJob} error={exportDetails.error?.message} />
      </Collapse>

      <Collapse in={showContent} timeout="auto" unmountOnExit>
        <Box mt={2}>
          {!['waiting_user_input', 'failed'].includes(jobBasic.status) && (
            <Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                typography="h5"
                sx={{ mb: 1 }}
              >
                <span>{t(statusLabelKeyMap[status] || 'exporting')}</span>
                <span>{progress.toFixed(1)}%</span>
              </Box>
              <LinearProgress
                value={progress}
                variant={exportJob?.totalItems ? 'determinate' : 'indeterminate'}
                color="secondary"
              />

              <br />
              <br />
              <Box component="b" mr={2} sx={{ userSelect: 'none' }}>
                {t('destination')}
              </Box>
              {destination ? (
                <Link href={destination} target="_blank" underline="hover" color="secondary">
                  {destination}
                </Link>
              ) : (
                <Box component="span">{t('creating_destination')}</Box>
              )}

              {!!exportJob?.totalItems && (
                <Box typography="h6" mt={2}>
                  {t('items_count', { count: exportJob.totalItems })}
                </Box>
              )}
            </Box>
          )}

          {hasIssues && exportJob && (
            <Box sx={{ my: 2 }}>
              <Alert severity="info">
                <Box>
                  {t('issues_found', { count: issuesCount })}
                  <Box display="flex" flexDirection="column" gap={1} component="ul" mr={0}>
                    <li>{t('include_items_with_issues')}</li>
                    <li>{t('visit_issues_tab')}</li>
                    <li>{t('cancel_export')}</li>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="justify-content"
                    alignItems="center"
                    gap={1}
                    mt={4}
                  >
                    <Box typography="h6" mr="auto">
                      {t('items_count', { count: exportJob.totalItems })}
                    </Box>
                    <Button
                      size="small"
                      onClick={() => onContinue.mutate({ exportJob: jobBasic, mode: 'cancel' })}
                    >
                      {t('cancel')}
                    </Button>
                    <FTGTooltip
                      title={issuesCount === exportJob.totalItems ? t('all_items_have_issues') : ''}
                    >
                      <Box>
                        <Button
                          color="info"
                          variant="contained"
                          size="small"
                          disabled={issuesCount === exportJob.totalItems}
                          onClick={() => onContinue.mutate({ exportJob: jobBasic, mode: 'skip' })}
                        >
                          {t('continue_skip')}
                        </Button>
                      </Box>
                    </FTGTooltip>
                    <Button
                      color="info"
                      variant="contained"
                      size="small"
                      onClick={() => onContinue.mutate({ exportJob: jobBasic, mode: 'retry' })}
                    >
                      {t('continue_try_again')}
                    </Button>
                  </Box>
                </Box>
              </Alert>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

type ExportJobsHeaderProps = {
  jobs: queries.ExportJobBasic[]
}

const ExportJobsHeader = (props: ExportJobsHeaderProps) => {
  const { t } = useTranslation('legacy', { keyPrefix: 'export' })
  const queryClient = useQueryClient()
  const [selectedJobIds, updateDialog, removeExportJob] = useExportJobs((state) => [
    state.exportDialog.selectedJobIds,
    state.updateDialog,
    state.remove,
  ])

  const archiveExportJobs = useMutation({
    mutationFn: exportAPI.archiveExportJobs,
    mutationKey: exportAPI.exportItemsKeys.archiveExportJobs,
    async onSuccess(data, variables) {
      // remove each export job from the status list (if found)
      variables.forEach((id) => {
        removeExportJob(id)
      })

      // refetch the export jobs
      await queryClient.refetchQueries({
        queryKey: exportAPI.exportItemsKeys.list,
      })

      updateDialog((prev) => {
        // clear the selected items
        prev.selectedJobIds = new Set()

        // if the expanded job is in the list, clear it
        if (prev.selectedJobId && variables.has(prev.selectedJobId)) {
          prev.selectedJobId = ''
        }
      })

      addSnack({
        message: t('jobs_removed'),
        severity: 'success',
      })
    },
    onError(error) {
      console.error(error)
      addSnack({
        message: t('error_removing_jobs'),
        severity: 'error',
      })
    },
  })

  const handleCheck = () => {
    updateDialog({
      selectedJobIds:
        selectedJobIds.size === 0 ? new Set(props.jobs.map((job) => job.id)) : new Set(),
    })
  }

  const handleRemove = () => {
    archiveExportJobs.mutate(selectedJobIds)
  }

  const isIndeterminate = selectedJobIds.size > 0 && selectedJobIds.size < props.jobs.length

  return (
    <Box mx={3}>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <LoadingButton
          color="tertiary"
          variant="contained"
          disabled={selectedJobIds.size === 0}
          loading={archiveExportJobs.isPending}
          onClick={handleRemove}
        >
          {t('delete_selected')}
        </LoadingButton>
      </Box>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Checkbox
          size="small"
          color="secondary"
          aria-label="toggle select all exports"
          onChange={handleCheck}
          checked={selectedJobIds.size > 0}
          indeterminate={isIndeterminate}
          sx={{ ml: 2 }}
        />
        <Box typography="body2" fontWeight={500} color="text.greyBlue">
          {props.jobs.length === 1
            ? t('selected_count', { selected: selectedJobIds.size, total: props.jobs.length })
            : t('selected_count_plural', {
                selected: selectedJobIds.size,
                total: props.jobs.length,
              })}
        </Box>
      </Box>
    </Box>
  )
}

const StatusTab = () => {
  const { t } = useTranslation('legacy', { keyPrefix: 'export' })
  const [selectedJobId, updateDialog] = useExportJobs((state) => [
    state.exportDialog.selectedJobId,
    state.updateDialog,
  ])

  const latestExports = exportAPI.useGetLatestExports()

  // auto select the first job, only if there is no selected job
  React.useEffect(() => {
    if (selectedJobId === null && latestExports.data?.exportJobs?.length) {
      const job = latestExports.data.exportJobs[0]
      updateDialog({ selectedJobId: job.id })
    }
  }, [selectedJobId, latestExports.data?.exportJobs])

  const isEmpty = latestExports.data?.exportJobs?.length === 0

  return (
    <>
      <Box sx={styles.tabTitle}>{t('status')}</Box>

      {latestExports.error && <Box px={3}>{t('failed_to_load_exports')}</Box>}

      {latestExports.isLoading && <Box px={3}>{t('loading')}</Box>}

      {isEmpty && <Box px={3}>{t('no_exports_found')}</Box>}

      {Boolean(latestExports.data?.exportJobs?.length) && (
        <>
          <ExportJobsHeader jobs={latestExports.data!.exportJobs} />
          {latestExports.data?.exportJobs.map((job) => {
            const isWebhookExport = job.exportType.includes('webhook/')
            return isWebhookExport ? (
              <WebhookExportJobRow
                key={job.id}
                jobBasic={job}
                isExpanded={job.id === selectedJobId}
              />
            ) : (
              <ExportJobRow key={job.id} jobBasic={job} isExpanded={job.id === selectedJobId} />
            )
          })}
        </>
      )}
    </>
  )
}

export default StatusTab
